import { Heading, InputField, SelectDropdown } from "@/common/components";
import { validate } from "@/utils/Validations";
import { memo, useEffect } from "@/common/hooks";
import { checkTwoFields } from "@/utils/Utilities";
import { Constants } from "@/utils/Constants";
import React from "react";

const ContactInformation = ({
  contactInfo,
  setContactInfo,
  states,
  debounceErrors,
  debounceState,
  errors,
  touched,
  setErrors,
  setTouched,
  labels,
  roleDropdown = false,
  rolePrimaryExists = false,
  confirmEmailField = true,
  showHeading = true,
  showNames = true,
  showCellPhone = true,
  showFaxNumber = true,
  showExtension = true,
  extensionMaxLength = 11,
  showExternalId = false,
  showCompanyName = false,
  showWebsite = false,
  showEmail = true,
  showServerHost = false,
  children,
  showZipCode = true,
  disabled = false,
}) => {
  const onChange = async (value, state) => {
    debounceState({ ...contactInfo, [state]: value }, setContactInfo);
    debounceErrors(
      {
        ...errors,
        [state]: await validate(value, [state]),
      },
      setErrors
    );
  };

  const errorProps = (state) => {
    return {
      value: contactInfo[state],
      error: errors[state],
      touched: touched[state],
      touch: () => setTouched({ ...touched, [state]: true }),
    };
  };

  useEffect(() => {
    debounceErrors(
      {
        ...errors,
        emailMatch: checkTwoFields(contactInfo.email, contactInfo.confirmEmail)
          ? labels?.emailMustMatch
          : "",
      },
      setErrors
    );
  }, [contactInfo.email, contactInfo.confirmEmail]);

  useEffect(() => {
    if (states && states?.states && !contactInfo?.stateId) {
      setContactInfo({ ...contactInfo, stateId: states?.states[0]?.id });
    }
  }, [states]);

  return (
    <section className="mb-5">
      {showHeading && (
        <Heading
          text={labels?.contactInformation}
          wrapperClassNames="h4 border-bottom-1 pb-1 mb-3"
          title={false}
        />
      )}
      {roleDropdown && (
        <SelectDropdown
          id="role"
          ariaLabel="Role"
          label="Role: *"
          dropdownData={
            rolePrimaryExists
              ? Constants.roleDropdownData.filter((e) => e.id !== "P")
              : Constants.roleDropdownData
          }
          wrapperClassName="col-md-5 col-12 me-3"
          selectClassName="w-100"
          select={(e) => {
            setContactInfo({
              ...contactInfo,
              roleCode: e,
            });
          }}
          value={contactInfo.roleCode}
          optionId="id"
          optionLabel="value"
          defaultOption={true}
        />
      )}
      {showExternalId && (
        <InputField
          id="externalId"
          label={labels?.externalId}
          maxLength="35"
          change={(e) => onChange(e, "externalId")}
          {...errorProps("externalId")}
          disabled={disabled}
        />
      )}
      {showCompanyName && (
        <InputField
          id="companyName"
          label={labels?.companyName}
          maxLength="35"
          change={(e) => onChange(e, "name")}
          {...errorProps("name")}
          disabled={disabled}
        />
      )}
      {showNames && (
        <div className="row m-0">
          <InputField
            id="firstName"
            label={labels?.firstName}
            wrapperClassName="col-12 col-lg-4 ps-0"
            maxLength="35"
            change={(e) => onChange(e, "firstName")}
            {...errorProps("firstName")}
            disabled={disabled}
          />
          <InputField
            id="middleName"
            label={labels?.middleName}
            maxLength="35"
            wrapperClassName="col-12 col-lg-4 ps-0"
            value={contactInfo.middleName}
            disabled={disabled}
            change={(e) => {
              debounceState(
                {
                  ...contactInfo,
                  middleName: e,
                },
                setContactInfo
              );
            }}
          />
          <InputField
            id="lastName"
            label={labels?.lastName}
            wrapperClassName="col-12 col-lg-4 p-0"
            maxLength="35"
            change={(e) => onChange(e, "lastName")}
            disabled={disabled}
            {...errorProps("lastName")}
          />
        </div>
      )}
      <InputField
        id="addressLine1"
        label={labels?.addressLine1}
        maxLength="35"
        change={(e) => onChange(e, "addressLine1")}
        disabled={disabled}
        {...errorProps("addressLine1")}
      />
      <InputField
        id="addressLine2"
        label={labels?.addressLine2}
        value={contactInfo.addressLine2}
        disabled={disabled}
        maxLength="35"
        change={(e) => {
          debounceState(
            {
              ...contactInfo,
              addressLine2: e,
            },
            setContactInfo
          );
        }}
      />
      <InputField
        id="addressLine3"
        label={labels?.addressLine3}
        maxLength="35"
        value={contactInfo.addressLine3}
        disabled={disabled}
        change={(e) => {
          debounceState(
            {
              ...contactInfo,
              addressLine3: e,
            },
            setContactInfo
          );
        }}
      />
      <div className="row m-0">
        <InputField
          id="city"
          label={labels?.city}
          wrapperClassName="col-12 col-lg-4 ps-0"
          disabled={disabled}
          maxLength="35"
          change={(e) => onChange(e, "city")}
          {...errorProps("city")}
        />
        <SelectDropdown
          id="state"
          ariaLabel="State"
          label={labels?.state}
          dropdownData={states?.states}
          disabled={disabled}
          wrapperClassName="col-12 col-lg-5 ps-0"
          selectClassName="w-100"
          select={(e) => {
            setContactInfo({
              ...contactInfo,
              stateId: e,
            });
          }}
          value={contactInfo.stateId}
          optionId="id"
          optionLabel="longName"
          prefixLabel="postalCode"
        />
        {showZipCode && (
          <InputField
            id="zipCode"
            type="text"
            label={labels?.zip}
            disabled={disabled}
            wrapperClassName="col-12 col-lg-3 pe-0"
            change={(e) => onChange(e, "zipCode")}
            {...errorProps("zipCode")}
          />
        )}
        {React.Children.map(children, (child) => {
          if (child.key === "ZipCode") {
            return child;
          }
          return null;
        })}
      </div>
      <div className="d-md-flex justify-content-between m-0">
        <InputField
          id="phoneNumber"
          label={labels?.phone}
          disabled={disabled}
          type="number"
          wrapperClassName={`col-12 col-md-8 col-lg-9 ps-0 me-lg-1  ${
            !showExtension ? "col-lg-12" : ""
          }`}
          change={(e) => onChange(e, "phoneNumber")}
          {...errorProps("phoneNumber")}
        />
        {showExtension && (
          <InputField
            id="phoneExt"
            disabled={disabled}
            label={labels?.ext}
            type="text"
            maxLength={extensionMaxLength}
            wrapperClassName="col-12 col-md-3 ps-0"
            value={contactInfo.phoneExt}
            change={(e) => {
              debounceState(
                {
                  ...contactInfo,
                  phoneExt: e,
                },
                setContactInfo
              );
            }}
          />
        )}
      </div>
      <div className="d-md-flex justify-content-between m-0">
        {showCellPhone && (
          <InputField
            disabled={disabled}
            id="cellPhoneNumber"
            label={labels?.cellPhone}
            type="number"
            wrapperClassName="col-12 col-md-7 col-lg-6 me-lg-1 "
            change={(e) => onChange(e, "cellPhoneNumber")}
            {...errorProps("cellPhoneNumber")}
          />
        )}
        {showFaxNumber && (
          <InputField
            id="faxNumber"
            label={labels?.fax}
            disabled={disabled}
            type="number"
            wrapperClassName={`col-12 col-md-4 col-lg-6 pe-0 ${
              !showCellPhone ? "col-lg-12" : ""
            }`}
            change={(e) => onChange(e, "faxNumber")}
            {...errorProps("faxNumber")}
          />
        )}
      </div>
      {showWebsite && (
        <InputField
          id="productManufacturerWebsite"
          disabled={disabled}
          label={labels?.productManufacturerWebsite}
          maxLength="255"
          wrapperClassName="col-md-12"
          change={(e) => onChange(e, "urlAddress")}
          {...errorProps("urlAddress")}
        />
      )}
      {showEmail && (
        <>
          <InputField
            id="email"
            label={labels?.email}
            disabled={disabled}
            change={(e) => onChange(e, "email")}
            {...errorProps("email")}
          />
          {confirmEmailField && (
            <InputField
              id="confirmEmail"
              disabled={disabled}
              label={labels?.confirmEmail}
              change={(e) => onChange(e, "confirmEmail")}
              {...errorProps("confirmEmail")}
              error={errors.confirmEmail || errors.emailMatch}
            />
          )}
        </>
      )}
      {React.Children.map(children, (child) => {
        if (child.key === "Email") return child;
        return null;
      })}
      {showServerHost && (
        <>
          <InputField
            id="serverHostName"
            disabled={disabled}
            label={labels?.serverHostName}
            value={contactInfo.serverHostName}
            maxLength="35"
            change={(e) => {
              debounceState(
                {
                  ...contactInfo,
                  serverHostName: e,
                },
                setContactInfo
              );
            }}
          />

          <InputField
            id="hostPortNumber"
            disabled={disabled}
            type="number"
            label={labels?.hostPortNumber}
            value={contactInfo.hostPortNumber}
            change={(e) => {
              debounceState(
                {
                  ...contactInfo,
                  hostPortNumber: e,
                },
                setContactInfo
              );
            }}
          />
        </>
      )}
    </section>
  );
};

export default memo(ContactInformation);
